import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Paper, InputBase, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const MapContainerStyled = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
  
  .blue-marker {
    background-color: #4285F4;
    border-radius: 50%;
    border: 2px solid white;
    width: 16px;
    height: 16px;
    box-shadow: 0 0 0 rgba(66, 133, 244, 0.4);
    animation: pulse 2s infinite;
  }
`;

const SearchBar = styled(Paper)`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 1000;
  padding: 2px 4px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 24px !important;
`;

const PressHoldOverlay = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: bold;
  color: #ff0000;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

// Component to handle map center changes
const ChangeMapView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { duration: 2 });
    }
  }, [center, map]);
  return null;
};

// Component to track map interactions
const MapInteractionTracker = ({ onInteractionStart, onInteractionEnd }) => {
  const interactionTimeoutRef = useRef(null);
  const isInteractingRef = useRef(false);
  
  const startInteraction = () => {
    if (!isInteractingRef.current) {
      isInteractingRef.current = true;
      if (onInteractionStart) onInteractionStart();
      console.log('Map interaction started');
    }
    
    // Clear any existing timeout
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }
  };
  
  const endInteraction = () => {
    // Set a timeout to end the interaction after a delay
    // This prevents the button from being activated immediately after interaction
    interactionTimeoutRef.current = setTimeout(() => {
      if (isInteractingRef.current) {
        isInteractingRef.current = false;
        if (onInteractionEnd) onInteractionEnd();
        console.log('Map interaction ended');
      }
    }, 1000); // 1 second delay before ending interaction
  };
  
  // Use Leaflet's event system
  useMapEvents({
    // Movement events
    dragstart: startInteraction,
    drag: startInteraction,
    dragend: endInteraction,
    zoomstart: startInteraction,
    zoom: startInteraction,
    zoomend: endInteraction,
    movestart: startInteraction,
    move: startInteraction,
    moveend: endInteraction,
    // Mouse events
    mousedown: startInteraction,
    mouseup: endInteraction,
    mousemove: startInteraction,
    // Touch events
    touchstart: startInteraction,
    touchmove: startInteraction,
    touchend: endInteraction,
    // Other events
    keypress: startInteraction,
    keydown: startInteraction,
    keyup: endInteraction,
  });
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);
  
  return null;
};

// Custom blue dot marker icon
const createBlueMarkerIcon = () => {
  return L.divIcon({
    className: 'blue-marker-icon',
    html: '<div class="blue-marker"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

const MapView = ({ onPressAndHold, onInteractionStart, onInteractionEnd }) => {
  const [position, setPosition] = useState([37.7749, -122.4194]); // Default to San Francisco
  const [pressTimer, setPressTimer] = useState(null);
  const [isPressing, setIsPressing] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationError, setLocationError] = useState('');
  const mapRef = useRef(null);

  // Get user's location
  useEffect(() => {
    // Check if permissions were previously denied
    checkPermissionState();
  }, []);

  const checkPermissionState = async () => {
    try {
      // For Chrome and browsers that support the permissions API
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        
        if (permissionStatus.state === 'denied') {
          // Permission already denied
          setLocationError('Location permission was denied. Please enable location services for this site in your browser settings.');
          setLocationDialogOpen(true);
          return;
        } else if (permissionStatus.state === 'prompt') {
          // Will prompt the user
          requestLocation();
          return;
        }
        
        // Listen for changes to permission state
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'denied') {
            setLocationError('Location permission was denied. Please enable location services for this site in your browser settings.');
            setLocationDialogOpen(true);
          } else if (permissionStatus.state === 'granted') {
            requestLocation();
          }
        };
        
        // If permission is already granted, request location
        if (permissionStatus.state === 'granted') {
          requestLocation();
        }
      } else {
        // For browsers that don't support the permissions API
        requestLocation();
      }
    } catch (error) {
      console.error('Error checking permission state:', error);
      requestLocation(); // Fallback to standard request
    }
  };

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setPosition([latitude, longitude]);
          setLocationDialogOpen(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = '';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission was denied. Please enable location services for this site in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please try again later.';
              break;
            case error.TIMEOUT:
              errorMessage = 'The request to get your location timed out. Please try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while trying to get your location.';
          }
          setLocationError(errorMessage);
          setLocationDialogOpen(true);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setLocationError('Geolocation is not supported by this browser.');
      setLocationDialogOpen(true);
    }
  };

  const handleRetryLocation = () => {
    requestLocation();
  };

  const handleOpenSettings = () => {
    // Provide instructions for different browsers
    alert('To enable location services:\n\n' +
          'Chrome: Click the lock icon in the address bar > Site settings > Location > Allow\n' +
          'Firefox: Click the lock icon in the address bar > Connection secure > More information > Permissions > Access your location > Allow\n' +
          'Safari: Go to Settings > Safari > Location Services > Enable');
  };

  // Handle press and hold events
  const handleTouchStart = (e) => {
    if (onInteractionStart) onInteractionStart();
    setIsPressing(true);
    const timer = setTimeout(() => {
      onPressAndHold();
      setIsPressing(false);
    }, 1000); // 1 second press and hold
    setPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (onInteractionEnd) onInteractionEnd();
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
    setIsPressing(false);
  };

  const handleMouseDown = (e) => {
    if (onInteractionStart) onInteractionStart();
    setIsPressing(true);
    const timer = setTimeout(() => {
      onPressAndHold();
      setIsPressing(false);
    }, 1000); // 1 second press and hold
    setPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (onInteractionEnd) onInteractionEnd();
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
    setIsPressing(false);
  };

  return (
    <>
      <SearchBar elevation={1}>
        <IconButton sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Find Musicians Near You..."
          inputProps={{ 'aria-label': 'find musicians' }}
        />
        <IconButton sx={{ p: '10px' }} aria-label="menu">
          <MenuIcon />
        </IconButton>
      </SearchBar>

      <MapContainerStyled>
        <MapContainer 
          center={position} 
          zoom={15} 
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} icon={createBlueMarkerIcon()} />
          <Circle 
            center={position} 
            radius={300} 
            pathOptions={{ 
              color: '#4285F4', 
              fillColor: '#4285F4', 
              fillOpacity: 0.2,
              weight: 2,
              opacity: 0.5
            }} 
          />
          <ChangeMapView center={position} />
          <MapInteractionTracker 
            onInteractionStart={onInteractionStart} 
            onInteractionEnd={onInteractionEnd} 
          />
        </MapContainer>
      </MapContainerStyled>

      {isPressing && (
        <PressHoldOverlay>PRESS AND HOLD</PressHoldOverlay>
      )}

      {/* Location Permission Dialog */}
      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        aria-labelledby="location-dialog-title"
      >
        <DialogTitle id="location-dialog-title">
          <Typography variant="h6" component="div" style={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon color="primary" style={{ marginRight: '8px' }} />
            Location Access Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {locationError}
          </Typography>
          <Typography variant="body2" style={{ marginTop: '16px' }}>
            JJ Muse uses your location to find musicians and events near you. Without location access, we'll use a default location instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)} color="primary">
            Use Default Location
          </Button>
          <Button onClick={handleRetryLocation} color="primary">
            Retry
          </Button>
          <Button onClick={handleOpenSettings} color="primary" variant="contained">
            How to Enable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapView;
